import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatNzDate, nzDateTimeParts } from '@promanage/core';
import { Button, Chip, Panel, PanelHeader } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { LoadFailed } from '../components/QueryState';
import { DataTable } from '../features/table/DataTable';
import { FilterChips, ToolbarSearch } from '../features/table/ListShell';
import { compareRows, type ColumnDef, type SortState } from '../features/table/columns';
import { sendState } from '../features/lists/sendState';
import { trpc, type RouterOutputs } from '../lib/trpc';

export const Route = createFileRoute('/communications/sends/$id')({
  component: SendPage,
});

type Send = RouterOutputs['bulkSend']['get'];
type Message = Send['messages'][number];
type Column = 'recipient' | 'about' | 'status' | 'from';
type Filter = 'all' | 'sent' | 'waiting' | 'skipped' | 'problems';

const SKIP_WORDS: Record<string, string> = {
  no_email: 'No email address',
  opted_out: 'Essential messages only',
  stopped: 'Address stopped after a bounce',
  missing_details: 'Missing details for the wording',
  duplicate: 'Already sent this email',
};

const when = (at: string) => {
  const parts = nzDateTimeParts(new Date(at));
  return `${formatNzDate(parts.date)} ${parts.time}`;
};

/** What happened to one person's copy, in the order that matters most. */
function status(message: Message): {
  label: string;
  tone: 'positive' | 'neutral' | 'warn';
  detail: string | null;
} {
  if (message.skipReason)
    return { label: 'Skipped', tone: 'neutral', detail: SKIP_WORDS[message.skipReason] ?? null };
  if (message.complainedAt)
    return { label: 'Marked as spam', tone: 'warn', detail: when(message.complainedAt) };
  if (message.bouncedAt) return { label: 'Bounced', tone: 'warn', detail: when(message.bouncedAt) };
  if (message.cancelledAt)
    return { label: 'Not sent', tone: 'warn', detail: message.cancellationReason };
  if (message.sentAt) return { label: 'Sent', tone: 'positive', detail: when(message.sentAt) };
  if (message.lastError) {
    return {
      label: message.attempts >= 5 ? 'Failed' : 'Retrying',
      tone: 'warn',
      detail: `${String(message.attempts)} ${message.attempts === 1 ? 'attempt' : 'attempts'}`,
    };
  }
  return { label: 'Queued', tone: 'neutral', detail: 'Goes out within five minutes' };
}

const matches: Record<Filter, (message: Message) => boolean> = {
  all: () => true,
  sent: (m) => m.sentAt !== null && m.bouncedAt === null && m.complainedAt === null,
  waiting: (m) => !m.skipReason && !m.sentAt && !m.cancelledAt,
  skipped: (m) => m.skipReason !== null,
  problems: (m) =>
    m.bouncedAt !== null ||
    m.complainedAt !== null ||
    m.cancelledAt !== null ||
    (m.lastError !== null && !m.sentAt),
};

const COLUMNS: readonly ColumnDef<Column, Message>[] = [
  {
    id: 'recipient',
    label: 'Recipient',
    width: '18rem',
    fixed: true,
    defaultVisible: true,
    sortValue: (m) => m.recipientName.toLowerCase(),
    render: (m) => (
      <div className="min-w-0">
        <span className="block truncate font-semibold">{m.recipientName}</span>
        <span className="block truncate text-[0.75rem] text-ink-subtle">
          {m.recipient ?? 'No email address'}
        </span>
      </div>
    ),
  },
  {
    id: 'about',
    label: 'About',
    width: '14rem',
    defaultVisible: true,
    sortValue: (m) => m.subjectLabel,
    render: (m) => <span className="block truncate">{m.subjectLabel ?? '—'}</span>,
  },
  {
    id: 'status',
    label: 'Status',
    width: '18rem',
    defaultVisible: true,
    sortValue: (m) => status(m).label,
    render: (m) => {
      const s = status(m);
      return (
        <span className="flex min-w-0 items-center gap-2">
          <Chip tone={s.tone}>{s.label}</Chip>
          {s.detail ? (
            <span className="truncate text-[0.75rem] text-ink-muted">{s.detail}</span>
          ) : null}
        </span>
      );
    },
  },
  {
    id: 'from',
    label: 'From',
    width: '16rem',
    defaultVisible: true,
    sortValue: (m) => m.fromName,
    render: (m) => <span className="block truncate">{m.fromName ?? '—'}</span>,
  },
];

/**
 * One bulk email: what it said, and what happened to every person it was meant for.
 *
 * THE ROW PER PERSON IS THE POINT. "Did the tenants at Rimu Ave get the water notice?" is answered
 * here — sent at 10:04, bounced, skipped because she asked for essential messages only — rather
 * than by scrolling an inbox's sent folder that the agency does not have.
 */
function SendPage() {
  const { id } = Route.useParams();
  const send = useQuery({
    ...trpc.bulkSend.get.queryOptions({ id }, { staleTime: 10_000 }),
    meta: { errorTitle: 'Could not load that email' },
    // Keep an eye on it while it is still going out.
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && data.messages.some((m) => !m.skipReason && !m.sentAt && !m.cancelledAt)
        ? 30_000
        : false;
    },
  });
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortState<Column>>({ column: 'recipient', direction: 'asc' });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (send.data?.messages ?? [])
      .filter(matches[filter])
      .filter((m) => !q || `${m.recipientName} ${m.recipient ?? ''}`.toLowerCase().includes(q))
      .sort((a, b) => compareRows(a, b, sort, COLUMNS));
  }, [send.data, filter, query, sort]);

  if (send.error) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Sent email" subtitle="Not found" />
        <LoadFailed retry={() => void send.refetch()} />
      </div>
    );
  }
  if (!send.data) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Sent email" subtitle="Loading…" />
        <Panel className="h-40" />
      </div>
    );
  }

  const data = send.data;
  const messages = data.messages;
  const queued = messages.filter((m) => !m.skipReason).length;
  const totals = {
    queued,
    sent: messages.filter((m) => m.sentAt).length,
    cancelled: messages.filter((m) => m.cancelledAt).length,
    failing: messages.filter((m) => m.lastError && !m.sentAt && !m.cancelledAt).length,
  };
  const state = sendState(totals);
  const count = (f: Filter) => messages.filter(matches[f]).length;

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title={data.subject}
        subtitle={`Sent by ${data.createdByName} on ${when(data.createdAt)}`}
        meta={<Chip tone={state.tone}>{state.label}</Chip>}
        actions={
          <Link to="/communications/sends">
            <Button size="sm" variant="secondary">
              All sent emails
            </Button>
          </Link>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel className="p-5 lg:col-span-2">
          <PanelHeader
            title="Message"
            subtitle="As written — each person’s copy had its merge fields filled in"
            className="p-0 pb-3"
          />
          <p className="text-[0.875rem] font-semibold">{data.subject}</p>
          <p className="mt-2 text-[0.875rem] whitespace-pre-wrap">{data.body}</p>
        </Panel>
        <Panel className="p-5">
          <PanelHeader title="Details" className="p-0 pb-3" />
          <dl className="flex flex-col gap-2 text-[0.8125rem]">
            <div>
              <dt className="text-ink-subtle">To</dt>
              <dd>
                {data.audience === 'tenants' ? 'Tenants' : 'Owners'}
                {data.listName ? ` of “${data.listName}”` : ''}
              </dd>
            </div>
            <div>
              <dt className="text-ink-subtle">Kind</dt>
              <dd>
                {data.essential
                  ? 'Essential — sent whatever each person’s preference'
                  : 'General update — people who asked for essential only were skipped'}
              </dd>
            </div>
            <div>
              <dt className="text-ink-subtle">From</dt>
              <dd>
                {data.senderMode === 'user' ? 'One member of staff' : 'Each property’s manager'}
              </dd>
            </div>
            <div>
              <dt className="text-ink-subtle">Attachments</dt>
              <dd>
                {data.attachments.length === 0
                  ? 'None'
                  : data.attachments.map((file) => file.fileName).join(', ')}
              </dd>
            </div>
            <div>
              <dt className="text-ink-subtle">Progress</dt>
              <dd className="tabular-nums">
                {totals.sent} of {queued} sent · {count('skipped')} skipped
                {count('problems') > 0 ? ` · ${String(count('problems'))} with a problem` : ''}
              </dd>
            </div>
          </dl>
        </Panel>
      </div>

      <Panel className="mt-4 overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 p-4">
          <FilterChips
            label="Filter recipients"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: `All ${String(messages.length)}` },
              { value: 'sent', label: `Sent ${String(count('sent'))}` },
              { value: 'waiting', label: `Waiting ${String(count('waiting'))}` },
              { value: 'skipped', label: `Skipped ${String(count('skipped'))}` },
              { value: 'problems', label: `Problems ${String(count('problems'))}` },
            ]}
          />
          <div className="ml-auto">
            <ToolbarSearch
              value={query}
              onChange={setQuery}
              placeholder="Search recipients"
              label="Search recipients"
            />
          </div>
        </div>
        <DataTable
          rows={rows}
          columns={COLUMNS}
          sort={sort}
          onSortChange={setSort}
          rowKey={(m) => m.id}
          caption={`Recipients of this email, one row per person. ${String(rows.length)} shown.`}
          minWidth="52rem"
        />
      </Panel>
    </div>
  );
}
